import type { ReactElement } from 'react';
import { Group, Pagination, Text } from '@mantine/core';

interface RankPaginationProps {
    /** Zero-based index of the first row on the current page. */
    start: number;
    /** Rows per page. */
    pageSize: number;
    /** Total rows across all pages — drives the caption count. */
    total: number;
    /** Number of pages. */
    pageCount: number;
    /** Current 1-based page. */
    page: number;
    onPageChange: (page: number) => void;
    /** Plural noun for the caption, e.g. `departments`. */
    unitPlural: string;
}

/**
 * The shared "Showing X–Y of Z {unit}" caption + page control used beneath both
 * ranked-list primitives ({@link RankedList} and {@link RankedBarList}). Callers
 * own the paging math and render this only when a list actually paginates.
 */
export default function RankPagination({ start, pageSize, total, pageCount, page, onPageChange, unitPlural }: RankPaginationProps): ReactElement {
    return (
        <Group justify="space-between" align="center" mt="sm" wrap="nowrap">
            <Text size="xs" c="dimmed">
                Showing {(start + 1).toLocaleString()}–{Math.min(start + pageSize, total).toLocaleString()} of {total.toLocaleString()} {unitPlural}
            </Text>
            <Pagination size="sm" total={pageCount} value={page} onChange={onPageChange} />
        </Group>
    );
}
